import { createApiClient, unwrap, toQuery } from '@barahpay/shared';
import type { ApiResponse, PagedResponse, TokenStore } from '@barahpay/shared';

/** Only public configuration reaches the bundle — never a secret (spec section 65). */
const baseUrl = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? 'http://localhost:5150';

const ACCESS_TOKEN_KEY = 'barahpay.admin.accessToken';
const REFRESH_TOKEN_KEY = 'barahpay.admin.refreshToken';

/**
 * The access token lives in memory only, so it is not readable from storage by injected script. The
 * refresh token is kept in sessionStorage rather than localStorage, so it dies with the tab and is not
 * a long-lived credential sitting on disk (spec section 65).
 */
class AdminTokenStore implements TokenStore {
  private accessToken: string | null = null;

  getAccessToken(): string | null {
    return this.accessToken;
  }

  getRefreshToken(): string | null {
    return sessionStorage.getItem(REFRESH_TOKEN_KEY);
  }

  setTokens(accessToken: string, refreshToken: string): void {
    this.accessToken = accessToken;
    sessionStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  }

  clear(): void {
    this.accessToken = null;
    sessionStorage.removeItem(REFRESH_TOKEN_KEY);
    sessionStorage.removeItem(ACCESS_TOKEN_KEY);
  }

  /** True when a refresh token exists, so the app can attempt to restore a session on reload. */
  canRestore(): boolean {
    return this.getRefreshToken() !== null;
  }
}

export const tokenStore = new AdminTokenStore();

let unauthorizedHandler: (() => void) | null = null;

export function onUnauthorized(handler: () => void): void {
  unauthorizedHandler = handler;
}

let currentLanguage = 'en';

export function setApiLanguage(language: string): void {
  currentLanguage = language;
}

export const http = createApiClient({
  baseUrl,
  tokenStore,
  getLanguage: () => currentLanguage,
  onUnauthorized: () => unauthorizedHandler?.(),
});

/** GET returning the section 51 envelope. */
export async function get<T>(url: string, params?: Record<string, unknown>): Promise<T> {
  const response = await http.get<ApiResponse<T>>(url, { params: params ? toQuery(params) : undefined });
  return unwrap<T>(response.data);
}

/** GET returning a paged envelope. */
export async function getPaged<T>(url: string, params?: Record<string, unknown>): Promise<PagedResponse<T>> {
  return get<PagedResponse<T>>(url, params);
}

export async function post<T>(url: string, body?: unknown, params?: Record<string, unknown>): Promise<T> {
  const response = await http.post<ApiResponse<T>>(url, body, { params: params ? toQuery(params) : undefined });
  return unwrap<T>(response.data);
}

export async function put<T>(url: string, body?: unknown): Promise<T> {
  const response = await http.put<ApiResponse<T>>(url, body);
  return unwrap<T>(response.data);
}

export async function del<T>(url: string): Promise<T> {
  const response = await http.delete<ApiResponse<T>>(url);
  return unwrap<T>(response.data);
}

/** Downloads a report export and hands the browser a file (spec section 74). */
export async function download(url: string, params: Record<string, unknown>, fallbackName: string): Promise<void> {
  const response = await http.get(url, { params: toQuery(params), responseType: 'blob' });

  const disposition = response.headers['content-disposition'] as string | undefined;
  const match = disposition?.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  const filename = match?.[1] ? decodeURIComponent(match[1]) : fallbackName;

  const blobUrl = URL.createObjectURL(response.data as Blob);
  const anchor = document.createElement('a');
  anchor.href = blobUrl;
  anchor.download = filename;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();

  // Release the object URL once the download has started.
  window.setTimeout(() => URL.revokeObjectURL(blobUrl), 10_000);
}

/** Uploads a settlement file (spec section 40). */
export async function upload<T>(url: string, file: File, params?: Record<string, unknown>): Promise<T> {
  const form = new FormData();
  form.append('file', file);

  const response = await http.post<ApiResponse<T>>(url, form, {
    params: params ? toQuery(params) : undefined,
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  return unwrap<T>(response.data);
}
